import { registerComponent } from "helpers";
import WYATTElement from "wyatt";

const FOCUSABLE_SELECTOR =
  'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])';

registerComponent(
  "disclosure-navigation",
  class DisclosureNavigation extends WYATTElement {
    constructor() {
      super();
      this.root = this;
      this.row = this.closest(".filter-bar") ?? this;
      this.controlledNodes = [];
      this.toggles = [
        ...this.querySelectorAll("button[aria-expanded][aria-controls]"),
      ];
      this.openIndex = null;

      this.toggles.forEach((node) => {
        const controlledId = node.getAttribute("aria-controls");
        const controlledNav = this.querySelector(`#${controlledId}`);

        if (controlledNav) {
          this.controlledNodes.push(controlledNav);
          // set initial expanded state
          node.setAttribute("aria-expanded", "false");
        }
      });

      // The row query also picks up buttons rendered outside this element
      // (e.g. "More Filters") that belong to the same horizontal row.
      this.navItems = [
        ...this.row.querySelectorAll(
          "button[aria-expanded][aria-controls], [data-filter-nav-item]",
        ),
      ];

      // The row and document outlive this element across filter re-renders,
      // so those listeners are torn down in disconnectedCallback.
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.addEventListener("click", this.onClick.bind(this));
      this.row.addEventListener("keydown", this.onKeyDown.bind(this), {
        signal,
      });
      document.addEventListener("click", this.onBlur.bind(this), { signal });
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.abortController.abort();
    }

    controlFocus(keyEvent, nodes, index, { horizontal = false } = {}) {
      const { key } = keyEvent;
      if (key === "ArrowUp" || (horizontal && key === "ArrowLeft")) {
        keyEvent.preventDefault();
        nodes[Math.max(0, index - 1)]?.focus();
      } else if (key === "ArrowDown" || (horizontal && key === "ArrowRight")) {
        keyEvent.preventDefault();
        nodes[Math.min(nodes.length - 1, index + 1)]?.focus();
      } else if (key === "Home") {
        keyEvent.preventDefault();
        nodes[0]?.focus();
      } else if (key === "End") {
        keyEvent.preventDefault();
        nodes[nodes.length - 1]?.focus();
      }
    }

    getPanelFocusables(index) {
      return [
        ...this.controlledNodes[index].querySelectorAll(FOCUSABLE_SELECTOR),
      ].filter((el) => !el.disabled);
    }

    onClick(event) {
      const toggle = event.target.closest(
        "button[aria-expanded][aria-controls]",
      );
      const index = this.toggles.indexOf(toggle);
      if (index === -1) {
        return;
      }
      const expanded = toggle.getAttribute("aria-expanded") === "true";
      this.toggleExpand(index, !expanded);
    }

    onKeyDown(event) {
      const navIndex = this.navItems.indexOf(event.target);
      const inPanel =
        this.openIndex !== null &&
        this.controlledNodes[this.openIndex].contains(event.target);
      if (navIndex === -1 && !inPanel) {
        return;
      }

      if (event.key === "Escape") {
        if (this.openIndex !== null) {
          const toggle = this.toggles[this.openIndex];
          this.toggleExpand(this.openIndex, false);
          toggle.focus();
        }
        return;
      }

      if (navIndex > -1) {
        if (this.openIndex === navIndex && event.key === "ArrowDown") {
          event.preventDefault();
          this.getPanelFocusables(this.openIndex)[0]?.focus();
        } else {
          this.controlFocus(event, this.navItems, navIndex, {
            horizontal: true,
          });
        }
        return;
      }

      this.panelFocus(event);
    }

    panelFocus(keyEvent) {
      const options = this.getPanelFocusables(this.openIndex);
      const index = options.indexOf(document.activeElement);

      if (keyEvent.key === "ArrowUp" && index === 0) {
        keyEvent.preventDefault();
        this.toggles[this.openIndex].focus();
        return;
      }
      this.controlFocus(keyEvent, options, index);
    }

    onBlur(event) {
      const rootFocused = this.contains(event.target);
      if (!rootFocused && this.openIndex !== null) {
        this.toggleExpand(this.openIndex, false);
      }
    }

    toggleExpand(index, isExpanded) {
      if (this.openIndex !== index) {
        this.toggleExpand(this.openIndex, false);
      }
      if (this.toggles[index]) {
        this.openIndex = isExpanded ? index : null;
        this.toggles[index].setAttribute("aria-expanded", isExpanded);
        this.toggleNav(this.controlledNodes[index], isExpanded);
      }
    }

    toggleNav(node, isShown) {
      if (node) {
        node.style.display = isShown ? "flex" : "none";
      }
    }
  },
);
